import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Chip, alpha } from '@mui/material';
import type { FilterChip } from './filterChips';

/** Alpha levels applied to `theme.filters.chipColor` for the border and delete icon. */
const TOKEN_ALPHA = { border: 0.12, delete: 0.4 } as const;

interface FilterCriteriaTokensProps<T> {
  chips: FilterChip<T>[];
  /** Remove one criterion (clears that key from the edited filter state). */
  onRemove: (key: keyof T) => void;
  /** Accessible name for each remove button (i18n'd by the caller). */
  removeLabel?: string;
}

/**
 * Removable "active criteria" tokens — a thin, agnostic presentation over
 * `buildFilterChips` output. Renders nothing when there are no chips.
 */
export function FilterCriteriaTokens<T>({
  chips,
  onRemove,
  removeLabel,
}: FilterCriteriaTokensProps<T>) {
  if (chips.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {chips.map(chip => (
        <Chip
          key={String(chip.key)}
          label={chip.text}
          size="small"
          onDelete={() => onRemove(chip.key)}
          deleteIcon={<CancelIcon titleAccess={removeLabel} />}
          sx={theme => ({
            borderRadius: theme.borderRadius.pill,
            height: 26,
            fontSize: theme.typography.fontSizeLabel,
            fontWeight: theme.typography.fontWeightBold,
            bgcolor: theme.filters.chipBgColor,
            border: `1px solid ${alpha(theme.filters.chipColor, TOKEN_ALPHA.border)}`,
            color: theme.filters.chipColor,
            '& .MuiChip-deleteIcon': {
              fontSize: '0.9rem',
              color: alpha(theme.filters.chipColor, TOKEN_ALPHA.delete),
              '&:hover': { color: theme.filters.chipColor },
            },
          })}
        />
      ))}
    </Box>
  );
}
